/**
 * features/hr/StaffProfile.tsx
 * ------------------------------------------------------------------
 * HR DOMAIN — Staff Profile v1 (demo).
 *
 * Five-tab employee dashboard modelled on the academic StudentProfile:
 *
 *   Tab 1 — Overview      identity, contact, residency, link to teacher
 *                         record (the single bridge to academic)
 *   Tab 2 — Compliance    documents on file, add document, mark
 *                         renewed (auto-versions into history chain)
 *   Tab 3 — Clocking      timesheet rows for this staff member
 *   Tab 4 — Leave         this staff member's leave history + balance
 *   Tab 5 — Contracts &   active contract, edit dates, salary band
 *           Payroll       and provider reference inline
 *   Tab 6 — Appraisals    review cycles
 *
 * Reached via #/staffProfile/<staffId>.
 * ------------------------------------------------------------------ */
import { useState } from 'react';
import {
  ArrowLeft,
  Pencil,
  User,
  ShieldCheck,
  Clock,
  CalendarDays,
  FileSignature,
  Star,
  Plus,
  Save,
  Loader2,
  RefreshCw,
  Mail,
  Phone,
  Building,
  Briefcase,
  Cake,
  GraduationCap,
  Wallet,
  Coins,
} from 'lucide-react';
import { GradientHero, SoftCard, StatChip, AccentButton, glassFieldClass } from '@/features/finance/ui';
import { FormModal } from '@/components/ui/glass';
import { fmtPula } from '@/lib/hrPayroll';
import { Spinner } from '@/components/ui/primitives';
import { useData } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { getPermissions } from '@/lib/permissions';
import { useHrData } from '@/features/hr/useHrData';
import { hr } from '@/features/hr/hrWrite';
import AttendanceCalendar from '@/features/hr/AttendanceCalendar';
import { LeaveApplicationsList } from '@/features/hr/LeaveApplications';
import EditStaffModal from '@/features/hr/EditStaffModal';
import PayslipsTab from '@/features/hr/PayslipsTab';
import LeaveBalances from '@/features/hr/LeaveBalances';
import {
  staffFullName,
  staffInitials,
  docHealth,
  daysUntil,
  summariseStaffCompliance,
  DOC_TYPE_LABELS,
  STAFF_CATEGORY_LABELS,
  RESIDENCY_LABELS,
  LEAVE_TYPE_LABELS,
  type HrStaff,
  type HrComplianceDoc,
  type HrContract,
  type HrLeaveRequest,
  type HrAttendance,
  type ComplianceDocType,
  type ComplianceHistoryEntry,
  type DocHealth,
  type AppraisalStatus,
  type ContractType,
  type EndOfServiceBenefit,
  END_OF_SERVICE_BENEFIT_LABELS,
} from '@/lib/hr';
import { salesFor } from '@/lib/leaveEncashment';
import { dayVerdict, fmtMinutes, fmtWorked, summarise, type ClockingRules } from '@/lib/clockingRules';
import { useClockingRules } from '@/features/hr/useClockingRules';
import type { EncashmentLike } from '@/lib/hrLeave';

type ProfileTab =
  | 'overview'
  | 'compliance'
  | 'clocking'
  | 'leave'
  | 'contract'
  | 'basicpay'
  | 'payroll'
  | 'appraisals';

const TABS: { id: ProfileTab; label: string; icon: typeof User }[] = [
  { id: 'overview', label: 'Overview', icon: User },
  { id: 'compliance', label: 'Compliance', icon: ShieldCheck },
  { id: 'clocking', label: 'Clocking', icon: Clock },
  { id: 'leave', label: 'Leave', icon: CalendarDays },
  { id: 'contract', label: 'Contract', icon: FileSignature },
  { id: 'basicpay', label: 'Basic Pay & Allowances', icon: Coins },
  { id: 'payroll', label: 'Payroll', icon: Wallet },
  { id: 'appraisals', label: 'Appraisals', icon: Star },
];

const HEALTH_BADGE: Record<DocHealth, { label: string; cls: string }> = {
  ok: { label: 'Compliant', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
  expiring: { label: 'Renew soon', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  expired: { label: 'Expired', cls: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
  missing: { label: 'Missing', cls: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
};

const STATUS_BADGE_APPRAISAL: Record<AppraisalStatus, string> = {
  draft: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  in_review: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
};

function fmtDate(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
}

function expiryNote(doc: HrComplianceDoc): string {
  const d = daysUntil(doc.expiryDate);
  if (d === null) return 'No expiry';
  if (d < 0) return `expired ${Math.abs(d)} day${Math.abs(d) === 1 ? '' : 's'} ago`;
  if (d === 0) return 'expires today';
  return `in ${d} day${d === 1 ? '' : 's'}`;
}

interface Props {
  staffId: string;
  onBack: () => void;
}

export default function StaffProfile({ staffId, onBack }: Props) {
  const data = useHrData();
  const { teachers } = useData();
  const { userRoles } = useAuth();
  const perms = getPermissions(userRoles);
  const clockingRules = useClockingRules();
  const [tab, setTab] = useState<ProfileTab>('overview');
  const [editing, setEditing] = useState(false);

  if (data.loading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner label="Loading staff profile…" />
      </div>
    );
  }

  const staff = data.staffById(staffId);
  if (!staff) {
    return (
      <div className="space-y-4">
        <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
          <ArrowLeft size={16} /> Back
        </button>
        <SoftCard hover={false}>
          <p className="text-sm text-gray-500">No staff record found for id <code>{staffId}</code>.</p>
        </SoftCard>
      </div>
    );
  }

  const linkedTeacher = staff.linkedTeacherId
    ? teachers.find((t) => t.id === staff.linkedTeacherId)
    : undefined;

  const myDocs = data.complianceDocs.filter((d) => d.staffId === staff.id);
  const myLeave = data.leave.filter((l) => l.staffId === staff.id);
  const myAttendance = data.attendance
    .filter((a) => a.staffId === staff.id)
    .sort((a, b) => b.date.localeCompare(a.date));
  const myAllContracts = data.contracts.filter((c) => c.staffId === staff.id);
  const myContract = myAllContracts.find((c) => c.active);
  const myContractHistory = myAllContracts
    .filter((c) => !c.active)
    .sort((a, b) => b.startDate.localeCompare(a.startDate));
  const myPayroll = data.payrollRefs.find((p) => p.staffId === staff.id);
  const myPayrollProfile = data.payrollProfiles.find((p) => p.staffId === staff.id);
  const myDeductions = data.deductions.filter((d) => d.staffId === staff.id);
  const myPayslips = data.payslips.filter((p) => p.staffId === staff.id);
  const myLeaveAllocation = data.leaveAllocations.find((a) => a.staffId === staff.id);
  const myAppraisals = data.appraisals.filter((a) => a.staffId === staff.id);

  // Approved-leave date set for this staff (overlays the calendar)
  const leaveDates = new Set<string>();
  for (const l of myLeave) {
    if (l.status !== 'approved') continue;
    const start = new Date(l.startDate);
    const end = new Date(l.endDate);
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      leaveDates.add(d.toISOString().slice(0, 10));
    }
  }

  const summary = summariseStaffCompliance(staff, data.complianceDocs, data.complianceMatrix);
  const worstBadge = HEALTH_BADGE[summary.worst];
  // For the demo, anyone with canAccessHR (hr or admin) can approve.
  // The school will tighten this tomorrow.
  const canApprove = perms.canAccessHR;

  return (
    <div className="space-y-6">
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
      >
        <ArrowLeft size={16} /> Back to staff
      </button>

      <GradientHero
        icon={User}
        title={staffFullName(staff)}
        subtitle={`${staff.jobTitle}${staff.department ? ` · ${staff.department}` : ''} · #${staff.employeeNo}`}
        actions={
          <div className="flex items-center gap-2">
            <span className={`text-xs font-medium px-3 py-1.5 rounded-xl ${worstBadge.cls}`}>
              {worstBadge.label}
            </span>
            {canApprove && (
              <AccentButton icon={Pencil} onClick={() => setEditing(true)}>
                Edit
              </AccentButton>
            )}
          </div>
        }
      />

      {/* Tab bar */}
      <SoftCard pad="p-1.5" hover={false}>
        <div className="flex flex-wrap gap-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  active
                    ? 'bg-[var(--accent-strong,#1b3a6b)] text-white font-medium'
                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                <Icon size={14} />
                {t.label}
              </button>
            );
          })}
        </div>
      </SoftCard>

      {tab === 'overview' && (
        <OverviewTab
          staff={staff}
          linkedTeacherName={linkedTeacher ? linkedTeacher.displayName : undefined}
          summary={summary}
          myDocs={myDocs}
          myLeave={myLeave}
          myAttendance={myAttendance}
          myContract={myContract}
          myContractHistory={myContractHistory}
        />
      )}
      {tab === 'compliance' && <ComplianceTab staff={staff} docs={myDocs} />}
      {tab === 'clocking' && <ClockingTab attendance={myAttendance} leaveDates={leaveDates} rules={clockingRules.rulesFor(staff.category)} />}
      {tab === 'leave' && <LeaveTab staff={staff} leave={myLeave} allocation={myLeaveAllocation} canApprove={canApprove} encashments={salesFor(data.leaveEncashments, staff.id)} />}
      {tab === 'contract' && <ContractTab staff={staff} contract={myContract} history={myContractHistory} payroll={myPayroll} />}
      {tab === 'basicpay' && (
        <SoftCard hover={false}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-800 dark:text-gray-100">Basic pay &amp; allowances</h3>
            <span className="text-xs text-gray-400 dark:text-gray-500">Entered on the Payroll workspace → Basic Pay &amp; Allowances</span>
          </div>
          {myPayrollProfile && (myPayrollProfile.basicSalary ?? 0) > 0 ? (
            <div className="flex flex-wrap gap-2">
              <StatChip label="Basic salary" value={fmtPula(myPayrollProfile.basicSalary)} />
              {(myPayrollProfile.allowances ?? []).map((a, i) => (
                <StatChip key={i} label={a.type} value={fmtPula(a.amount)} />
              ))}
              <StatChip label="Gratuity" value={myPayrollProfile.gratuityEligible ? `${myPayrollProfile.gratuityRatePct}%` : 'Not eligible'} />
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No basic pay set yet. Enter it on the Payroll workspace under <strong>Basic Pay &amp; Allowances</strong>.
            </p>
          )}
        </SoftCard>
      )}
      {tab === 'payroll' && (
        <PayslipsTab
          staff={staff}
          profile={myPayrollProfile}
          deductions={myDeductions}
          activeTaxTable={data.activeTaxTable}
          leaveAllocation={myLeaveAllocation}
          leave={myLeave}
          contracts={myAllContracts}
          payslips={myPayslips}
          settings={data.payrollSettings}
          canEdit={canApprove}
        />
      )}
      {tab === 'appraisals' && <AppraisalsTab staff={staff} appraisals={myAppraisals} />}

      {editing && <EditStaffModal staff={staff} onClose={() => setEditing(false)} />}
    </div>
  );
}

/* ==================================================================
   OVERVIEW TAB — hero head, at-a-glance, employment timeline
   ================================================================== */
function OverviewTab({
  staff,
  linkedTeacherName,
  summary,
  myDocs,
  myLeave,
  myAttendance,
  myContract,
  myContractHistory,
}: {
  staff: HrStaff;
  linkedTeacherName?: string;
  summary: ReturnType<typeof summariseStaffCompliance>;
  myDocs: HrComplianceDoc[];
  myLeave: HrLeaveRequest[];
  myAttendance: HrAttendance[];
  myContract: HrContract | undefined;
  myContractHistory: HrContract[];
}) {
  const clockingRules = useClockingRules();
  // Today's status
  const todayKey = new Date().toISOString().slice(0, 10);
  const todayStored = myAttendance.find((a) => a.date === todayKey);
  const todayV = todayStored ? dayVerdict(todayStored, clockingRules.rulesFor(staff.category)) : null;
  const todayRec = todayStored && todayV ? { ...todayStored, status: todayV.status } : undefined;

  // Currently on leave?
  const now = Date.now();
  const onLeaveNow = myLeave.find((l) => {
    if (l.status !== 'approved') return false;
    return new Date(l.startDate).getTime() <= now && new Date(l.endDate).getTime() >= now;
  });

  // Tenure
  const startDate = new Date(staff.startDate);
  const tenureYears = (Date.now() - startDate.getTime()) / (365.25 * 86400000);
  const tenureLabel =
    tenureYears < 1
      ? `${Math.round(tenureYears * 12)} months`
      : `${tenureYears.toFixed(1)} years`;

  // Next document due
  const nextDoc = (() => {
    const upcoming = myDocs
      .filter((d) => d.expiryDate)
      .map((d) => ({ d, days: daysUntil(d.expiryDate) }))
      .filter((x) => x.days !== null && x.days >= 0)
      .sort((a, b) => (a.days ?? 0) - (b.days ?? 0));
    return upcoming[0];
  })();

  // Combined contract timeline (history + active), most recent first
  const allContracts = [...myContractHistory, ...(myContract ? [myContract] : [])]
    .sort((a, b) => b.startDate.localeCompare(a.startDate));

  return (
    <div className="space-y-4">
      {/* HERO HEAD */}
      <SoftCard hover={false}>
        <div className="flex flex-col md:flex-row md:items-center gap-5">
          {/* Avatar */}
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[var(--accent-from,#2d5295)] to-[var(--accent-to,#1b3a6b)] text-white flex items-center justify-center text-2xl font-bold shrink-0">
            {staffInitials(staff)}
          </div>

          {/* Identity + chips */}
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
              {staffFullName(staff)}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {staff.jobTitle}
              {staff.department ? ` · ${staff.department}` : ''}
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <Pill icon={Briefcase} label={STAFF_CATEGORY_LABELS[staff.category]} />
              <Pill icon={Cake} label={`${tenureLabel} at LKC`} />
              <Pill icon={User} label={RESIDENCY_LABELS[staff.residency]} />
              {linkedTeacherName && (
                <Pill icon={GraduationCap} label="Linked to academic record" tone="blue" />
              )}
            </div>
          </div>

          {/* Quick contact */}
          <div className="md:border-l md:border-gray-100 md:dark:border-gray-700 md:pl-5 space-y-1 text-sm shrink-0">
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
              <Mail size={13} className="text-gray-400" />
              <span className="truncate">{staff.email ?? '—'}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
              <Phone size={13} className="text-gray-400" />
              <span>{staff.phone ?? '—'}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-400 text-xs">
              <span>#{staff.employeeNo}</span>
            </div>
          </div>
        </div>
      </SoftCard>

      {/* AT A GLANCE — three signals + four counters */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Today */}
        <SignalCard
          title="Today"
          icon={Clock}
          tone={onLeaveNow ? 'blue' : todayRec?.status === 'late' ? 'amber' : todayRec?.status === 'absent' ? 'red' : todayRec ? 'emerald' : 'slate'}
          primary={
            onLeaveNow
              ? `On ${LEAVE_TYPE_LABELS[onLeaveNow.type].toLowerCase()} leave`
              : todayRec?.status === 'absent'
                ? 'Absent'
                : todayRec?.status === 'late'
                  ? `Late ${fmtMinutes(todayV?.lateMinutes ?? 0)}`
                  : todayRec
                    ? 'Present'
                    : 'No punch yet'
          }
          secondary={
            onLeaveNow
              ? `until ${fmtDate(onLeaveNow.endDate)}`
              : todayV?.firstIn
                ? `In ${todayV.firstIn}${todayV.lastOut ? ` · Out ${todayV.lastOut}` : ''}`
                : ''
          }
        />

        {/* Contract */}
        <SignalCard
          title="Contract"
          icon={FileSignature}
          tone={myContract ? 'emerald' : 'slate'}
          primary={
            myContract
              ? `${CONTRACT_LABELS[myContract.type]} · v${myContract.version}`
              : 'No active contract'
          }
          secondary={
            myContract?.endDate
              ? `ends ${fmtDate(myContract.endDate)}`
              : myContract
                ? 'open ended'
                : ''
          }
        />

        {/* Next document due */}
        <SignalCard
          title="Next document due"
          icon={ShieldCheck}
          tone={
            nextDoc
              ? (nextDoc.days ?? 999) <= 30
                ? 'red'
                : (nextDoc.days ?? 999) <= 90
                  ? 'amber'
                  : 'emerald'
              : 'slate'
          }
          primary={
            nextDoc ? DOC_TYPE_LABELS[nextDoc.d.docType] : 'Nothing on the horizon'
          }
          secondary={
            nextDoc?.days !== undefined && nextDoc.days !== null
              ? nextDoc.days === 0
                ? 'expires today'
                : `in ${nextDoc.days} days`
              : ''
          }
        />
      </div>

      {/* Stat row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatChip tone="emerald" value={summary.ok} label="Docs healthy" />
        <StatChip tone="gold" value={summary.expiring} label="Expiring" />
        <StatChip tone="red" value={summary.expired + summary.missingMandatory} label="At risk" />
        <StatChip tone="blue" value={myLeave.length} label="Leave records" />
      </div>

      {/* Employment timeline */}
      {allContracts.length > 0 && (
        <SoftCard hover={false}>
          <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
            <Clock size={16} /> Employment timeline
          </h3>
          <ol className="relative space-y-4 ml-1">
            <span className="absolute left-2 top-1 bottom-1 w-px bg-gray-200 dark:bg-gray-700" aria-hidden="true" />
            {allContracts.map((c, idx) => {
              const isLatest = idx === 0 && c.active;
              return (
                <li key={c.id} className="relative pl-7">
                  <span
                    className={`absolute left-0 top-1 w-4 h-4 rounded-full border-2 ${
                      isLatest
                        ? 'bg-emerald-500 border-emerald-500'
                        : c.active
                          ? 'bg-emerald-400 border-emerald-400'
                          : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600'
                    }`}
                  />
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                        {CONTRACT_LABELS[c.type]}
                        <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400 font-semibold align-middle">
                          v{c.version}
                        </span>
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {fmtDate(c.startDate)} – {c.endDate ? fmtDate(c.endDate) : 'open'}
                      </p>
                    </div>
                    <span
                      className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-lg font-semibold ${
                        c.active
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                          : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {c.active ? 'Current' : 'Archived'}
                    </span>
                  </div>
                </li>
              );
            })}
          </ol>
        </SoftCard>
      )}
    </div>
  );
}

const CONTRACT_LABELS: Record<ContractType, string> = {
  permanent: 'Permanent',
  fixed_term: 'Fixed term',
  probation: 'Probation',
  casual: 'Casual',
};

function Pill({
  icon: Icon,
  label,
  tone,
}: {
  icon: typeof User;
  label: string;
  tone?: 'blue';
}) {
  const cls =
    tone === 'blue'
      ? 'bg-blue-50 text-blue-700 border-blue-100 dark:bg-blue-900/30 dark:text-[var(--accent-link-dark,#93c5fd)] dark:border-blue-900/50'
      : 'bg-gray-50 text-gray-600 border-gray-100 dark:bg-gray-700/50 dark:text-gray-300 dark:border-gray-700';
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border ${cls}`}>
      <Icon size={12} />
      {label}
    </span>
  );
}

function SignalCard({
  title,
  icon: Icon,
  tone,
  primary,
  secondary,
}: {
  title: string;
  icon: typeof Clock;
  tone: 'emerald' | 'amber' | 'red' | 'blue' | 'slate';
  primary: string;
  secondary?: string;
}) {
  const TONES: Record<typeof tone, string> = {
    emerald: 'text-emerald-700 dark:text-emerald-300',
    amber: 'text-amber-700 dark:text-amber-300',
    red: 'text-red-700 dark:text-red-300',
    blue: 'text-blue-700 dark:text-[var(--accent-link-dark,#93c5fd)]',
    slate: 'text-gray-600 dark:text-gray-300',
  };
  return (
    <SoftCard hover={false} pad="p-4">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-gray-400 dark:text-gray-500 font-semibold mb-2">
        <Icon size={13} />
        {title}
      </div>
      <p className={`text-base font-semibold ${TONES[tone]}`}>{primary}</p>
      {secondary && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          {secondary}
        </p>
      )}
    </SoftCard>
  );
}



/* ==================================================================
   COMPLIANCE TAB — add doc + mark renewed
   ================================================================== */
export function ComplianceTab({ staff, docs }: { staff: HrStaff; docs: HrComplianceDoc[] }) {
  const [adding, setAdding] = useState(false);
  const [renewingId, setRenewingId] = useState<string | null>(null);

  // Group docs by health status
  const grouped = (() => {
    const order: DocHealth[] = ['expired', 'expiring', 'ok', 'missing'];
    const map = new Map<DocHealth, HrComplianceDoc[]>();
    for (const doc of docs) {
      const h = docHealth(doc);
      const arr = map.get(h) ?? [];
      arr.push(doc);
      map.set(h, arr);
    }
    return order
      .map((h) => ({ status: h, items: map.get(h) ?? [] }))
      .filter((g) => g.items.length > 0);
  })();

  const SECTION_META: Record<DocHealth, { label: string; tone: 'red' | 'amber' | 'emerald' | 'slate' }> = {
    expired: { label: 'Expired — action required', tone: 'red' },
    expiring: { label: 'Renew soon', tone: 'amber' },
    ok: { label: 'Compliant', tone: 'emerald' },
    missing: { label: 'Missing mandatory', tone: 'red' },
  };

  const TONE_TEXT: Record<'red' | 'amber' | 'emerald' | 'slate', string> = {
    red: 'text-red-700 dark:text-red-300',
    amber: 'text-amber-700 dark:text-amber-300',
    emerald: 'text-emerald-700 dark:text-emerald-300',
    slate: 'text-gray-500 dark:text-gray-400',
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Permits, clearances, and certificates on file for {staff.firstName}.
        </p>
        <AccentButton icon={Plus} onClick={() => setAdding(true)}>
          Add document
        </AccentButton>
      </div>

      {docs.length === 0 ? (
        <SoftCard hover={false}>
          <p className="text-sm text-gray-500 py-4 text-center">
            No documents on file yet.
          </p>
        </SoftCard>
      ) : (
        <div className="space-y-5">
          {grouped.map((group) => {
            const meta = SECTION_META[group.status];
            return (
              <section key={group.status} className="space-y-2">
                <div className="flex items-center gap-2 px-1">
                  <span className={`text-[11px] uppercase tracking-wider font-semibold ${TONE_TEXT[meta.tone]}`}>
                    {meta.label}
                  </span>
                  <span className="text-[11px] text-gray-400">{group.items.length}</span>
                </div>
                <div className="space-y-2">
                  {group.items.map((doc) => {
                    const h = docHealth(doc);
                    const badge = HEALTH_BADGE[h];
                    return (
                      <SoftCard key={doc.id} pad="p-4" hover={false}>
                        <div className="flex items-center gap-3">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                              {DOC_TYPE_LABELS[doc.docType]}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {doc.issuingAuthority ?? '—'}
                              {doc.referenceNumber ? ` · ${doc.referenceNumber}` : ''}
                              {' · '}
                              {expiryNote(doc)}
                            </p>
                            {doc.history && doc.history.length > 0 && (
                              <p className="text-[11px] text-gray-400 mt-1">
                                Renewal history: {doc.history.length} previous version
                                {doc.history.length === 1 ? '' : 's'}
                              </p>
                            )}
                          </div>
                          {(h === 'expired' || h === 'expiring') && (
                            <button
                              onClick={() => setRenewingId(doc.id)}
                              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-[var(--accent-link-dark,#93c5fd)]"
                            >
                              <RefreshCw size={12} /> Renew
                            </button>
                          )}
                          <span className={`text-xs font-medium px-2.5 py-1 rounded-lg whitespace-nowrap ${badge.cls}`}>
                            {badge.label}
                          </span>
                        </div>
                      </SoftCard>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {adding && <AddDocModal staffId={staff.id} onClose={() => setAdding(false)} />}
      {renewingId && (
        <RenewDocModal
          doc={docs.find((d) => d.id === renewingId)!}
          onClose={() => setRenewingId(null)}
        />
      )}
    </div>
  );
}

function AddDocModal({ staffId, onClose }: { staffId: string; onClose: () => void }) {
  const [docType, setDocType] = useState<ComplianceDocType>('permission_to_teach');
  const [refNo, setRefNo] = useState('');
  const [authority, setAuthority] = useState('');
  const [issueDate, setIssueDate] = useState(new Date().toISOString().slice(0, 10));
  const [expiryDate, setExpiryDate] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setSaving(true);
    setErr(null);
    try {
      await hr.complianceDocs.add({
        staffId,
        docType,
        docTypeLabel: DOC_TYPE_LABELS[docType],
        issuingAuthority: authority.trim() || undefined,
        referenceNumber: refNo.trim() || undefined,
        issueDate: new Date(issueDate).toISOString(),
        expiryDate: expiryDate ? new Date(expiryDate).toISOString() : undefined,
        status: 'active',
        notes: notes.trim() || undefined,
      });
      onClose();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title="Add compliance document" onClose={onClose}>
      <Field label="Document type">
        <select
          value={docType}
          onChange={(e) => setDocType(e.target.value as ComplianceDocType)}
          className={glassFieldClass('py-2')}
        >
          {(Object.keys(DOC_TYPE_LABELS) as ComplianceDocType[]).map((d) => (
            <option key={d} value={d}>
              {DOC_TYPE_LABELS[d]}
            </option>
          ))}
        </select>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Reference number">
          <input value={refNo} onChange={(e) => setRefNo(e.target.value)} className={glassFieldClass('py-2')} />
        </Field>
        <Field label="Issuing authority">
          <input value={authority} onChange={(e) => setAuthority(e.target.value)} className={glassFieldClass('py-2')} />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Issue date">
          <input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} className={glassFieldClass('py-2')} />
        </Field>
        <Field label="Expiry date (optional)">
          <input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} className={glassFieldClass('py-2')} />
        </Field>
      </div>
      <Field label="Notes (optional)">
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className={glassFieldClass('py-2')} />
      </Field>
      {err && <p className="text-sm text-red-600">{err}</p>}
      <ModalFooter onCancel={onClose} onSave={save} saving={saving} canSave={true} />
    </Modal>
  );
}

function RenewDocModal({ doc, onClose }: { doc: HrComplianceDoc; onClose: () => void }) {
  const [refNo, setRefNo] = useState('');
  const [issueDate, setIssueDate] = useState(new Date().toISOString().slice(0, 10));
  const [expiryDate, setExpiryDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setSaving(true);
    setErr(null);
    try {
      const priorEntry: ComplianceHistoryEntry = {
        referenceNumber: doc.referenceNumber ?? '',
        issueDate: doc.issueDate ?? '',
        expiryDate: doc.expiryDate,
        fileUrl: doc.fileUrl,
        supersededAt: new Date().toISOString(),
      };
      const history = [...(doc.history ?? []), priorEntry];
      await hr.complianceDocs.update(doc.id, {
        referenceNumber: refNo.trim() || doc.referenceNumber,
        issueDate: new Date(issueDate).toISOString(),
        expiryDate: expiryDate ? new Date(expiryDate).toISOString() : undefined,
        status: 'active',
        history,
      });
      onClose();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title={`Renew ${DOC_TYPE_LABELS[doc.docType]}`} onClose={onClose}>
      <p className="text-xs text-gray-500 mb-2">
        The previous version is preserved in the renewal history.
      </p>
      <Field label="New reference number">
        <input value={refNo} onChange={(e) => setRefNo(e.target.value)} placeholder={doc.referenceNumber ?? ''} className={glassFieldClass('py-2')} />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="New issue date">
          <input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} className={glassFieldClass('py-2')} />
        </Field>
        <Field label="New expiry date">
          <input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} className={glassFieldClass('py-2')} />
        </Field>
      </div>
      {err && <p className="text-sm text-red-600">{err}</p>}
      <ModalFooter onCancel={onClose} onSave={save} saving={saving} canSave={true} saveLabel="Save renewal" />
    </Modal>
  );
}

/* ==================================================================
   CLOCKING TAB — sub-tabs: month-at-a-glance / summary
   ================================================================== */
type ClockingSubTab = 'month' | 'summary';

function ClockingTab({
  attendance,
  leaveDates,
  rules,
}: {
  attendance: ReturnType<typeof useHrData>['attendance'];
  leaveDates: Set<string>;
  /** This person's clocking rules (their staff type's start times). */
  rules: ClockingRules;
}) {
  const [sub, setSub] = useState<ClockingSubTab>('month');

  // Worked out from the punches against the school's start times
  // (lib/clockingRules), never the status stored at upload.
  const stats = summarise(attendance, rules);

  return (
    <div className="space-y-4">
      {/* Sub-tab pills */}
      <div className="inline-flex bg-gray-100 dark:bg-gray-700/50 rounded-xl p-1 gap-1">
        {[
          { id: 'month' as const, label: 'Month at a glance' },
          { id: 'summary' as const, label: 'Summary & history' },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setSub(t.id)}
            className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
              sub === t.id
                ? 'bg-white dark:bg-gray-800 text-[var(--accent-strong,#1b3a6b)] dark:text-[var(--accent-link-dark,#93c5fd)] font-medium shadow-sm'
                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {sub === 'month' && (
        <SoftCard hover={false}>
          <AttendanceCalendar attendance={attendance} leaveDates={leaveDates} rules={rules} />
        </SoftCard>
      )}

      {sub === 'summary' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <StatChip tone="emerald" value={stats.daysIn} label="Days in" />
            <StatChip tone="gold" value={stats.lateDays} label="Days late" />
            <StatChip tone="gold" value={stats.lateDays ? fmtMinutes(Math.round(stats.lateMinutes / stats.lateDays)) : '—'} label="Average lateness" />
            <StatChip tone="slate" value={stats.averageIn ?? '—'} label="Usual arrival (weekdays)" />
            <StatChip tone="red" value={stats.missingPunches} label="Missed clock-outs" />
          </div>

          <SoftCard pad="p-0" hover={false}>
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
              <h3 className="font-semibold text-gray-800 dark:text-gray-100">
                Recent days
              </h3>
            </div>
            {attendance.length === 0 ? (
              <p className="px-5 py-8 text-center text-sm text-gray-500">
                No timesheet records yet.
              </p>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                {attendance.slice(0, 20).map((rec) => {
                  const v = dayVerdict(rec, rules);
                  const dateLabel = new Date(rec.date).toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' });
                  return (
                    <li key={rec.id} className="px-5 py-3 flex items-center gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                          {dateLabel}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          In {v.firstIn ?? '—'} · Out {v.lastOut ?? '—'} · {v.missingPunch ? fmtWorked(v).toLowerCase() : `worked ${fmtWorked(v)}`}
                        </p>
                      </div>
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-lg whitespace-nowrap ${v.late
                        ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}
                      >
                        {v.late ? `Late ${fmtMinutes(v.lateMinutes)}` : v.missingIn ? 'No clock-in' : v.firstIn ? 'On time' : v.status === 'leave' ? 'On leave' : 'Absent'}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </SoftCard>
        </div>
      )}
    </div>
  );
}

/* ==================================================================
   LEAVE TAB — prominent balance + applications (view-only here; new
   applications are raised from the Leave nav tab)
   ================================================================== */
function LeaveTab({
  staff,
  leave,
  allocation,
  canApprove,
  encashments,
}: {
  staff: HrStaff;
  leave: ReturnType<typeof useHrData>['leave'];
  allocation: ReturnType<typeof useHrData>['leaveAllocations'][number] | undefined;
  canApprove: boolean;
  encashments: EncashmentLike[];
}) {
  const totals = (() => {
    let approved = 0, daysTaken = 0;
    for (const l of leave) {
      if (l.status === 'approved') {
        approved += 1;
        daysTaken += l.days;
      }
    }
    return { approved, daysTaken };
  })();

  return (
    <div className="space-y-4">
      {/* Balance up top — the headline figure */}
      <LeaveBalances
        staff={staff}
        leave={leave}
        allocation={allocation}
        canEdit={canApprove}
        encashments={encashments}
      />

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <StatChip tone="blue" value={leave.length} label="Total applications" />
        <StatChip tone="emerald" value={totals.approved} label="Approved" />
        <StatChip tone="slate" value={totals.daysTaken} label="Days taken" />
      </div>

      {/* History (view-only; raise new applications from the Leave tab) */}
      <LeaveApplicationsList
        staff={staff}
        applications={leave}
        canApprove={canApprove}
      />
    </div>
  );
}

/* ==================================================================
   CONTRACT & PAYROLL TAB
   ================================================================== */
/** Pretty contract date, e.g. "30 Jun 2026". */
function fmtContractDate(d: string): string {
  if (!d) return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** "2 years 5 months" between two dates; open-ended when no end. */
function contractDuration(startISO: string, endISO: string): string {
  if (!startISO) return '—';
  if (!endISO) return 'Open-ended';
  const s = new Date(startISO);
  const e = new Date(endISO);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime()) || e < s) return '—';
  let months = (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth());
  if (e.getDate() < s.getDate()) months -= 1;
  months = Math.max(0, months);
  const years = Math.floor(months / 12);
  const rem = months % 12;
  const parts: string[] = [];
  if (years) parts.push(`${years} year${years === 1 ? '' : 's'}`);
  if (rem) parts.push(`${rem} month${rem === 1 ? '' : 's'}`);
  if (!parts.length) parts.push('under a month');
  return parts.join(' ');
}

/** Time-to-expiry label + tone for an end date. */
function contractRemaining(endISO: string): { label: string; tone: string } {
  const e = new Date(endISO);
  if (Number.isNaN(e.getTime())) return { label: '', tone: 'text-gray-500' };
  const days = Math.round((e.getTime() - Date.now()) / 86400000);
  if (days < 0) return { label: `expired ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} ago`, tone: 'text-red-600 dark:text-red-400' };
  if (days === 0) return { label: 'expires today', tone: 'text-amber-600 dark:text-amber-400' };
  if (days <= 60) return { label: `expires in ${days} day${days === 1 ? '' : 's'}`, tone: 'text-amber-600 dark:text-amber-400' };
  return { label: `expires in ${days} days`, tone: 'text-emerald-600 dark:text-emerald-400' };
}

export function ContractTab({
  staff,
  contract,
  history,
  payroll,
}: {
  staff: HrStaff;
  contract: ReturnType<typeof useHrData>['contracts'][number] | undefined;
  history: ReturnType<typeof useHrData>['contracts'];
  payroll: ReturnType<typeof useHrData>['payrollRefs'][number] | undefined;
}) {
  const [type, setType] = useState<ContractType>(contract?.type ?? 'permanent');
  const [benefit, setBenefit] = useState<EndOfServiceBenefit | ''>(contract?.endOfServiceBenefit ?? '');
  const [startDate, setStartDate] = useState(contract?.startDate?.slice(0, 10) ?? '');
  const [endDate, setEndDate] = useState(contract?.endDate?.slice(0, 10) ?? '');
  const [contractSaving, setContractSaving] = useState(false);
  const [contractSaved, setContractSaved] = useState(false);
  const [renewing, setRenewing] = useState(false);

  const [salaryBand, setSalaryBand] = useState(payroll?.salaryBand ?? '');
  const [providerRef, setProviderRef] = useState(payroll?.providerRef ?? '');
  const [payrollSaving, setPayrollSaving] = useState(false);
  const [payrollSaved, setPayrollSaved] = useState(false);

  async function saveContract() {
    setContractSaving(true);
    setContractSaved(false);
    try {
      const patch = {
        type,
        startDate: startDate ? new Date(startDate).toISOString() : undefined,
        endDate: endDate ? new Date(endDate).toISOString() : undefined,
        endOfServiceBenefit: benefit || undefined,
      };
      if (contract) {
        await hr.contracts.update(contract.id, patch);
      } else {
        await hr.contracts.add({
          staffId: staff.id,
          ...patch,
          version: 1,
          active: true,
        });
      }
      setContractSaved(true);
      setTimeout(() => setContractSaved(false), 1500);
    } finally {
      setContractSaving(false);
    }
  }

  async function savePayroll() {
    setPayrollSaving(true);
    setPayrollSaved(false);
    try {
      await hr.payrollRefs.set(staff.id, {
        staffId: staff.id,
        salaryBand: salaryBand.trim() || undefined,
        providerRef: providerRef.trim() || undefined,
      });
      setPayrollSaved(true);
      setTimeout(() => setPayrollSaved(false), 1500);
    } finally {
      setPayrollSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      {/* Active contract + payroll side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SoftCard hover={false}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
              <FileSignature size={16} /> Active contract
            </h3>
            {contract && (
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 font-semibold">
                v{contract.version} · current
              </span>
            )}
          </div>
          <div className="space-y-3">
            {startDate && (
              <div className="rounded-xl bg-gray-50 dark:bg-gray-700/40 px-3 py-2.5 text-sm space-y-1">
                <div className="flex justify-between gap-3">
                  <span className="text-gray-500 dark:text-gray-400">Term</span>
                  <span className="font-medium text-gray-800 dark:text-gray-100 text-right">
                    {fmtContractDate(startDate)} → {endDate ? fmtContractDate(endDate) : 'open-ended'}
                  </span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-gray-500 dark:text-gray-400">Duration</span>
                  <span className="text-gray-800 dark:text-gray-100">{contractDuration(startDate, endDate)}</span>
                </div>
                {endDate && (
                  <div className="flex justify-between gap-3">
                    <span className="text-gray-500 dark:text-gray-400">Status</span>
                    <span className={`font-medium ${contractRemaining(endDate).tone}`}>{contractRemaining(endDate).label}</span>
                  </div>
                )}
              </div>
            )}
            <Field label="Type">
              <select
                value={type}
                onChange={(e) => setType(e.target.value as ContractType)}
                className={glassFieldClass('py-2')}
              >
                <option value="permanent">Permanent</option>
                <option value="fixed_term">Fixed term</option>
                <option value="probation">Probation</option>
                <option value="casual">Casual</option>
              </select>
            </Field>
            <Field label="End-of-service benefit (on exit)">
              <select
                value={benefit}
                onChange={(e) => setBenefit(e.target.value as EndOfServiceBenefit | '')}
                className={glassFieldClass('py-2')}
              >
                <option value="">— not set —</option>
                <option value="gratuity">{END_OF_SERVICE_BENEFIT_LABELS.gratuity}</option>
                <option value="severance">{END_OF_SERVICE_BENEFIT_LABELS.severance}</option>
                <option value="none">{END_OF_SERVICE_BENEFIT_LABELS.none}</option>
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Start date">
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={glassFieldClass('py-2')} />
              </Field>
              <Field label="End date">
                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={glassFieldClass('py-2')} />
              </Field>
            </div>
            <div className="flex items-center justify-between gap-2 pt-1">
              {contract && (
                <button
                  onClick={() => setRenewing(true)}
                  className="text-xs font-medium text-[var(--accent-link,#2d5295)] hover:underline dark:text-[var(--accent-link-dark,#93c5fd)]"
                >
                  Start new contract…
                </button>
              )}
              <div className="flex items-center gap-2 ml-auto">
                {contractSaved && <span className="text-xs text-emerald-600">Saved.</span>}
                <AccentButton icon={contractSaving ? Loader2 : Save} disabled={contractSaving} onClick={saveContract}>
                  {contractSaving ? 'Saving…' : 'Save contract'}
                </AccentButton>
              </div>
            </div>
          </div>
        </SoftCard>

        <SoftCard hover={false}>
          <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
            <Building size={16} /> Payroll reference
          </h3>
          <div className="space-y-3">
            <Field label="Salary band">
              <input value={salaryBand} onChange={(e) => setSalaryBand(e.target.value)} placeholder="e.g. C3" className={glassFieldClass('py-2')} />
            </Field>
            <Field label="Provider reference">
              <input value={providerRef} onChange={(e) => setProviderRef(e.target.value)} placeholder="id in your payroll system" className={glassFieldClass('py-2')} />
            </Field>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Export-only — no PAYE / payslip calculation here.
            </p>
            <div className="flex items-center justify-end gap-2 pt-1">
              {payrollSaved && <span className="text-xs text-emerald-600">Saved.</span>}
              <AccentButton icon={payrollSaving ? Loader2 : Save} disabled={payrollSaving} onClick={savePayroll}>
                {payrollSaving ? 'Saving…' : 'Save payroll'}
              </AccentButton>
            </div>
          </div>
        </SoftCard>
      </div>

      {/* Contract history */}
      <ContractHistory items={history} />

      {renewing && contract && (
        <RenewContractModal
          staff={staff}
          current={contract}
          onClose={() => setRenewing(false)}
        />
      )}
    </div>
  );
}

/* ==================================================================
   CONTRACT HISTORY — read-only list of previous contracts
   ================================================================== */
function ContractHistory({
  items,
}: {
  items: ReturnType<typeof useHrData>['contracts'];
}) {
  if (items.length === 0) {
    return (
      <SoftCard hover={false}>
        <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-2 flex items-center gap-2">
          <Clock size={16} /> Contract history
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 py-2">
          No previous contracts. This is the first contract on file.
        </p>
      </SoftCard>
    );
  }

  return (
    <SoftCard pad="p-0" hover={false}>
      <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
        <Clock size={16} className="text-gray-500" />
        <h3 className="font-semibold text-gray-800 dark:text-gray-100">
          Contract history
        </h3>
        <span className="text-xs text-gray-400 ml-1">
          ({items.length} previous)
        </span>
      </div>
      <ul className="divide-y divide-gray-100 dark:divide-gray-700">
        {items.map((c) => {
          const endedDays = c.endDate
            ? Math.round((Date.now() - new Date(c.endDate).getTime()) / 86400000)
            : null;
          const endedLabel = endedDays === null
            ? 'archived'
            : endedDays < 0
              ? `ends in ${Math.abs(endedDays)} day${Math.abs(endedDays) === 1 ? '' : 's'}`
              : endedDays < 30
                ? `ended ${endedDays} day${endedDays === 1 ? '' : 's'} ago`
                : endedDays < 365
                  ? `ended ${Math.floor(endedDays / 30)} month${Math.floor(endedDays / 30) === 1 ? '' : 's'} ago`
                  : `ended ${Math.floor(endedDays / 365)} year${Math.floor(endedDays / 365) === 1 ? '' : 's'} ago`;
          return (
            <li key={c.id} className="px-5 py-3 flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center shrink-0">
                <FileSignature size={14} className="text-gray-500" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                    {CONTRACT_TYPE_LABELS[c.type]}
                  </p>
                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400 font-semibold">
                    v{c.version}
                  </span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {fmtDate(c.startDate)} – {c.endDate ? fmtDate(c.endDate) : 'open'}
                  {' · '}
                  {endedLabel}
                </p>
              </div>
              <span className="text-xs font-medium px-2.5 py-1 rounded-lg bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 whitespace-nowrap">
                Expired
              </span>
            </li>
          );
        })}
      </ul>
    </SoftCard>
  );
}

/* ==================================================================
   RENEW CONTRACT MODAL — archives the current, creates a new one
   ================================================================== */
function RenewContractModal({
  staff,
  current,
  onClose,
}: {
  staff: HrStaff;
  current: ReturnType<typeof useHrData>['contracts'][number];
  onClose: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [newType, setNewType] = useState<ContractType>(current.type);
  const [newBenefit, setNewBenefit] = useState<EndOfServiceBenefit | ''>(current.endOfServiceBenefit ?? '');
  const [newStart, setNewStart] = useState(today);
  const [newEnd, setNewEnd] = useState('');
  const [archiveDate, setArchiveDate] = useState(today);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setSaving(true);
    setErr(null);
    try {
      // 1. Archive the current contract: mark inactive, stamp endDate
      await hr.contracts.update(current.id, {
        active: false,
        endDate: new Date(archiveDate).toISOString(),
      });
      // 2. Create a new active contract, incrementing the version
      await hr.contracts.add({
        staffId: staff.id,
        type: newType,
        startDate: new Date(newStart).toISOString(),
        endDate: newEnd ? new Date(newEnd).toISOString() : undefined,
        endOfServiceBenefit: newBenefit || undefined,
        version: (current.version ?? 1) + 1,
        active: true,
        notes: '',
      });
      onClose();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title="Start a new contract" onClose={onClose}>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
        The current contract will be archived and moved into the history below.
        A new version{' '}
        <span className="font-medium text-gray-700 dark:text-gray-200">
          v{(current.version ?? 1) + 1}
        </span>{' '}
        will become the active contract for {staff.firstName}.
      </p>
      <Field label="Archive current contract on">
        <input type="date" value={archiveDate} onChange={(e) => setArchiveDate(e.target.value)} className={glassFieldClass('py-2')} />
      </Field>
      <div className="border-t border-gray-100 dark:border-gray-700 pt-3">
        <p className="text-[11px] uppercase tracking-wider text-gray-400 mb-2">
          New contract
        </p>
        <Field label="Type">
          <select
            value={newType}
            onChange={(e) => setNewType(e.target.value as ContractType)}
            className={glassFieldClass('py-2')}
          >
            <option value="permanent">Permanent</option>
            <option value="fixed_term">Fixed term</option>
            <option value="probation">Probation</option>
            <option value="casual">Casual</option>
          </select>
        </Field>
        <Field label="End-of-service benefit (on exit)">
          <select
            value={newBenefit}
            onChange={(e) => setNewBenefit(e.target.value as EndOfServiceBenefit | '')}
            className={glassFieldClass('py-2')}
          >
            <option value="">— not set —</option>
            <option value="gratuity">{END_OF_SERVICE_BENEFIT_LABELS.gratuity}</option>
            <option value="severance">{END_OF_SERVICE_BENEFIT_LABELS.severance}</option>
            <option value="none">{END_OF_SERVICE_BENEFIT_LABELS.none}</option>
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <Field label="Start date">
            <input type="date" value={newStart} onChange={(e) => setNewStart(e.target.value)} className={glassFieldClass('py-2')} />
          </Field>
          <Field label="End date (optional)">
            <input type="date" value={newEnd} onChange={(e) => setNewEnd(e.target.value)} className={glassFieldClass('py-2')} />
          </Field>
        </div>
      </div>
      {err && <p className="text-sm text-red-600">{err}</p>}
      <ModalFooter onCancel={onClose} onSave={save} saving={saving} canSave={true} saveLabel="Archive & start new" />
    </Modal>
  );
}

/* Small lookup used by the history list above. */
const CONTRACT_TYPE_LABELS: Record<ContractType, string> = {
  permanent: 'Permanent',
  fixed_term: 'Fixed term',
  probation: 'Probation',
  casual: 'Casual',
};

/* ==================================================================
   APPRAISALS TAB
   ================================================================== */
function AppraisalsTab({
  staff,
  appraisals,
}: {
  staff: HrStaff;
  appraisals: ReturnType<typeof useHrData>['appraisals'];
}) {
  const data = useHrData();
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Sort: newest cycle first by cycle string descending (e.g. 2026-H2 > 2026-H1 > 2025-H2)
  const sorted = [...appraisals].sort((a, b) =>
    b.cycle.localeCompare(a.cycle),
  );

  // For the trend mini-chart: only completed cycles in chronological order
  const trend = [...appraisals]
    .filter((a) => a.status === 'completed' && a.rating)
    .sort((a, b) => a.cycle.localeCompare(b.cycle))
    .slice(-6); // last 6 cycles

  const avgRating = trend.length > 0
    ? (trend.reduce((s, a) => s + (a.rating ?? 0), 0) / trend.length).toFixed(1)
    : '—';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Performance review cycles for {staff.firstName}.
        </p>
        <AccentButton icon={Plus} onClick={() => setAdding(true)}>
          New cycle
        </AccentButton>
      </div>

      {/* Trend chart — only shown if there's something to plot */}
      {trend.length >= 2 && (
        <SoftCard hover={false}>
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="font-semibold text-gray-800 dark:text-gray-100">
                Rating trend
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Last {trend.length} completed cycles · average {avgRating}
              </p>
            </div>
          </div>
          <RatingTrend cycles={trend} />
        </SoftCard>
      )}

      {sorted.length === 0 ? (
        <SoftCard hover={false}>
          <p className="text-sm text-gray-500 py-4 text-center">
            No appraisals on file yet.
          </p>
        </SoftCard>
      ) : (
        <div className="space-y-2">
          {sorted.map((a) => {
            const reviewerStaff = a.reviewerId
              ? data.staffById(a.reviewerId)
              : undefined;
            return (
              <SoftCard key={a.id} hover={false} pad="p-4">
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                        Cycle {a.cycle}
                      </p>
                      <span
                        className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-lg font-semibold ${STATUS_BADGE_APPRAISAL[a.status]}`}
                      >
                        {a.status.replace('_', ' ')}
                      </span>
                      {a.rating && (
                        <span className="flex items-center gap-0.5 ml-1">
                          {[1, 2, 3, 4, 5].map((i) => (
                            <Star
                              key={i}
                              size={13}
                              className={i <= a.rating! ? 'text-amber-400 fill-amber-400' : 'text-gray-300 dark:text-gray-600'}
                            />
                          ))}
                        </span>
                      )}
                    </div>
                    {reviewerStaff && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Reviewer: {staffFullName(reviewerStaff)}
                      </p>
                    )}
                    {a.summary && editingId !== a.id && (
                      <p className="text-sm text-gray-600 dark:text-gray-300 mt-2 leading-relaxed">
                        {a.summary}
                      </p>
                    )}
                    {editingId === a.id ? (
                      <SummaryEditor
                        appraisalId={a.id}
                        initial={a.summary ?? ''}
                        onClose={() => setEditingId(null)}
                      />
                    ) : (
                      a.status === 'completed' && !a.summary && (
                        <button
                          onClick={() => setEditingId(a.id)}
                          className="text-xs text-[var(--accent-link,#2d5295)] hover:underline dark:text-[var(--accent-link-dark,#93c5fd)] mt-2"
                        >
                          + Add summary
                        </button>
                      )
                    )}
                    {a.summary && editingId !== a.id && a.status === 'completed' && (
                      <button
                        onClick={() => setEditingId(a.id)}
                        className="text-xs text-gray-400 hover:text-[var(--accent-link,#2d5295)] hover:underline mt-1"
                      >
                        Edit summary
                      </button>
                    )}
                  </div>
                </div>
              </SoftCard>
            );
          })}
        </div>
      )}

      {adding && <NewAppraisalModal staffId={staff.id} onClose={() => setAdding(false)} />}
    </div>
  );
}

/* Mini SVG line chart for ratings */
function RatingTrend({ cycles }: { cycles: ReturnType<typeof useHrData>['appraisals'] }) {
  if (cycles.length < 2) return null;
  const w = 600;
  const h = 80;
  const padX = 24;
  const padY = 12;
  const innerW = w - padX * 2;
  const innerH = h - padY * 2;

  const points = cycles.map((c, i) => {
    const x = padX + (i * innerW) / (cycles.length - 1);
    const r = c.rating ?? 0;
    const y = padY + innerH - ((r - 1) / 4) * innerH;
    return { x, y, rating: r, cycle: c.cycle };
  });

  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');

  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full" style={{ maxHeight: 120 }}>
        {/* Grid lines: 1, 3, 5 */}
        {[1, 3, 5].map((r) => {
          const y = padY + innerH - ((r - 1) / 4) * innerH;
          return (
            <line
              key={r}
              x1={padX}
              x2={w - padX}
              y1={y}
              y2={y}
              stroke="currentColor"
              className="text-gray-200 dark:text-gray-700"
              strokeDasharray="2 3"
            />
          );
        })}
        {/* Path */}
        <path d={path} fill="none" stroke="#d4a92c" strokeWidth="2" />
        {/* Points */}
        {points.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r="4" fill="#d4a92c" />
            <text
              x={p.x}
              y={p.y - 8}
              textAnchor="middle"
              className="text-[10px] fill-current text-gray-500 dark:text-gray-400"
            >
              {p.rating}
            </text>
            <text
              x={p.x}
              y={h - 2}
              textAnchor="middle"
              className="text-[9px] fill-current text-gray-400"
            >
              {p.cycle}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}

function SummaryEditor({
  appraisalId,
  initial,
  onClose,
}: {
  appraisalId: string;
  initial: string;
  onClose: () => void;
}) {
  const [value, setValue] = useState(initial);
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await hr.appraisals.update(appraisalId, { summary: value.trim() || undefined });
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mt-2 space-y-2">
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        rows={3}
        placeholder="Summary of the review — strengths, areas to develop, agreed objectives…"
        className={glassFieldClass('py-2 text-sm')}
        autoFocus
      />
      <div className="flex items-center gap-2 justify-end">
        <button
          onClick={onClose}
          className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-lg dark:text-gray-300 dark:hover:bg-gray-700"
        >
          Cancel
        </button>
        <button
          onClick={save}
          disabled={saving}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-[var(--accent-strong,#1b3a6b)] text-white hover:bg-[var(--accent-strong-hover,#162e52)] disabled:opacity-60"
        >
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
          {saving ? 'Saving…' : 'Save summary'}
        </button>
      </div>
    </div>
  );
}

function NewAppraisalModal({ staffId, onClose }: { staffId: string; onClose: () => void }) {
  const now = new Date();
  const half = now.getMonth() < 6 ? 'H1' : 'H2';
  const [cycle, setCycle] = useState(`${now.getFullYear()}-${half}`);
  const [summary, setSummary] = useState('');
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await hr.appraisals.add({
        staffId,
        cycle,
        status: 'in_review',
        summary: summary.trim() || undefined,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title="Start a new appraisal cycle" onClose={onClose}>
      <Field label="Cycle">
        <input value={cycle} onChange={(e) => setCycle(e.target.value)} className={glassFieldClass('py-2')} />
      </Field>
      <Field label="Notes (optional)">
        <textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={3} className={glassFieldClass('py-2')} />
      </Field>
      <ModalFooter onCancel={onClose} onSave={save} saving={saving} canSave={!!cycle.trim()} saveLabel="Start cycle" />
    </Modal>
  );
}

/* ==================================================================
   SHARED — modal + field
   ================================================================== */
function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <FormModal onClose={onClose} title={title} maxWidth="max-w-lg">
      <div className="space-y-4">{children}</div>
    </FormModal>
  );
}

function ModalFooter({
  onCancel,
  onSave,
  saving,
  canSave,
  saveLabel = 'Save',
}: {
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
  canSave: boolean;
  saveLabel?: string;
}) {
  return (
    <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100 dark:border-gray-700 -mx-5 -mb-5 px-5 py-4">
      <button
        onClick={onCancel}
        className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl dark:text-gray-300 dark:hover:bg-gray-700"
      >
        Cancel
      </button>
      <AccentButton icon={saving ? Loader2 : Save} disabled={!canSave || saving} onClick={onSave}>
        {saving ? 'Saving…' : saveLabel}
      </AccentButton>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">
        {label}
      </span>
      {children}
    </label>
  );
}
